import { useState, useEffect, useRef, createContext, useContext, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { I18nProvider } from './lib/i18n';
import Header from './components/Header';
import SmoothScroll from './components/SmoothScroll';
import ChatAgent from './components/ChatAgent';
import A11ySidebar from './components/A11ySidebar';
import CookieConsent from './components/CookieConsent';
import Home from './pages/Home';
import FullscreenModal from './components/FullscreenModal';
import ProjetosHub from './components/ProjetosHub';
import { AuthProvider } from './lib/useAuth';

// ── Lazy-loaded pages (code splitting) ───────────────────
const AcessibilidadeContent = lazy(() => import('./pages/AcessibilidadeContent'));
const Noticias = lazy(() => import('./pages/Noticias'));
const DEIAContent = lazy(() => import('./pages/DEIAContent'));
const SolucoesContent = lazy(() => import('./pages/SolucoesContent'));
const ArtigosContent = lazy(() => import('./pages/ArtigosContent'));
const EmFocoContent = lazy(() => import('./pages/EmFocoContent'));
const InstitutoContent = lazy(() => import('./pages/InstitutoContent'));
const AtuacaoContent = lazy(() => import('./pages/AtuacaoContent'));
const ContatoContent = lazy(() => import('./pages/ContatoContent'));

// Admin — heavy (recharts, tiptap, etc.) — only loaded on /admin/*
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads'));
const AdminMetricas = lazy(() => import('./pages/admin/AdminMetricas'));
const AdminNoticias = lazy(() => import('./pages/admin/AdminNoticias'));
const AdminParceiros = lazy(() => import('./pages/admin/AdminParceiros'));
const AdminPublicacoes = lazy(() => import('./pages/admin/AdminPublicacoes'));
const AdminNewsletter = lazy(() => import('./pages/admin/AdminNewsletter'));
const AdminContatos = lazy(() => import('./pages/admin/AdminContatos'));
const AdminArtigos = lazy(() => import('./pages/admin/AdminArtigos'));
const AdminTextos = lazy(() => import('./pages/admin/AdminTextos'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminHeroMedia = lazy(() => import('./pages/admin/AdminHeroMedia'));
const AdminImagens = lazy(() => import('./pages/admin/AdminImagens'));
const AdminProjetos = lazy(() => import('./pages/admin/AdminProjetos'));
const AdminDesignSystem = lazy(() => import('./pages/admin/AdminDesignSystem'));
import { Toaster } from './components/ui/sonner';
import { usePageTracking, useTrackModal, useScrollDepth, useWebVitals, useEngagement, track404 } from './lib/analytics';
import './App.css';

// Modal context
export const ModalContext = createContext();
export const useModal = () => useContext(ModalContext);

// Analytics wrapper (needs to be inside BrowserRouter for useLocation)
function AnalyticsTracker() {
  usePageTracking();
  useScrollDepth();
  useWebVitals();
  useEngagement();
  return null;
}

// 404 tracker — renders a branded not-found message and fires track404
function NotFoundTracker() {
  const { pathname } = useLocation();
  track404(pathname);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4 text-center">
      <div className="section-divider mx-auto mb-8" />
      <h1 className="font-oswald text-5xl md:text-7xl uppercase mb-4">404</h1>
      <p className="font-manrope text-black/50 text-lg mb-8">Página não encontrada</p>
      <a
        href="/"
        className="inline-flex items-center gap-2 h-12 px-8 bg-black text-white font-oswald uppercase text-sm tracking-widest hover:bg-[#FFC107] hover:text-black transition-all btn-spring"
      >
        Voltar ao início
      </a>
    </div>
  );
}

// Track modal opens/closes
function ModalTracker({ activeModal }) {
  useTrackModal('instituto', activeModal === 'instituto');
  useTrackModal('deia', activeModal === 'deia');
  useTrackModal('emfoco', activeModal === 'emfoco');
  useTrackModal('artigos', activeModal === 'artigos');
  useTrackModal('solucoes', activeModal === 'solucoes');
  useTrackModal('acessibilidade', activeModal === 'acessibilidade');
  useTrackModal('contato', activeModal === 'contato');
  useTrackModal('projetos', activeModal === 'atuacao' || activeModal === 'projetos' || (activeModal && activeModal.startsWith('projeto-')));
  return null;
}

function App() {
  const [activeModal, setActiveModal] = useState(null);
  const [emfocoBreadcrumb, setEmfocoBreadcrumb] = useState([{ label: 'Em Foco' }]);
  const [artigosBreadcrumb, setArtigosBreadcrumb] = useState([{ label: 'Artigos' }]);
  const emfocoScrollRef = useRef(null);
  const artigosScrollRef = useRef(null);

  const openModal = useCallback((modal) => {
    setActiveModal(modal);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    // Reset breadcrumbs when modal closes
    setEmfocoBreadcrumb([{ label: 'Em Foco' }]);
    setArtigosBreadcrumb([{ label: 'Artigos' }]);
  }, []);

  // Listen for custom modal events (from Footer etc)
  useEffect(() => {
    const handler = (e) => openModal(e.detail);
    window.addEventListener('open-modal', handler);
    return () => window.removeEventListener('open-modal', handler);
  }, [openModal]);

  // Auto-open modal from URL hash
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const hashMap = { instituto: 'instituto', atuacao: 'atuacao', contato: 'contato', deia: 'deia', emfoco: 'emfoco', artigos: 'artigos', acessibilidade: 'acessibilidade', solucoes: 'solucoes' };
    if (hashMap[hash]) {
      setTimeout(() => openModal(hashMap[hash]), 300);
    } else if (hash.startsWith('projeto-')) {
      setTimeout(() => openModal(hash), 300);
    }
  }, [openModal]);

  return (
    <HelmetProvider>
    <AuthProvider>
      <I18nProvider>
      <ModalContext.Provider value={{ openModal, closeModal, activeModal }}>
        <BrowserRouter>
          <AnalyticsTracker />
          <ModalTracker activeModal={activeModal} />
          <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-[#FFC107] focus:text-black focus:font-bold focus:text-sm">
            Pular para o conteúdo principal
          </a>
          <SmoothScroll />
          <ChatAgent />
          <A11ySidebar />
          <Suspense fallback={<div className="h-screen bg-black flex items-center justify-center"><div className="w-8 h-[2px] bg-[#FFC107] animate-pulse" /></div>}>
          <Routes>
            {/* Admin routes — no Header, own layout */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/admin/metricas" element={<AdminMetricas />} />
            <Route path="/admin/noticias-cms" element={<AdminNoticias />} />
            <Route path="/admin/parceiros" element={<AdminParceiros />} />
            <Route path="/admin/publicacoes" element={<AdminPublicacoes />} />
            <Route path="/admin/newsletter" element={<AdminNewsletter />} />
            <Route path="/admin/contatos" element={<AdminContatos />} />
            <Route path="/admin/artigos" element={<AdminArtigos />} />
            <Route path="/admin/textos" element={<AdminTextos />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/hero" element={<AdminHeroMedia />} />
            <Route path="/admin/imagens" element={<AdminImagens />} />
            <Route path="/admin/projetos" element={<AdminProjetos />} />
            <Route path="/admin/design-system" element={<AdminDesignSystem />} />

            {/* Public routes */}
            <Route path="*" element={
              <div className="App">
                <Header />
                <main id="main-content">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/noticias" element={<Home autoOpenModal="emfoco" />} />
                    <Route path="/artigos" element={<Home autoOpenModal="artigos" />} />
                    <Route path="/deia" element={<Home autoOpenModal="deia" />} />
                    <Route path="/noticias/:slug" element={<Home autoOpenModal="article-url" />} />
                    <Route path="/artigos/:slug" element={<Home autoOpenModal="article-url" />} />
                    <Route path="/instituto" element={<Home autoOpenModal="instituto" />} />
                    <Route path="/atuacao" element={<Home autoOpenModal="atuacao" />} />
                    <Route path="/contato" element={<Home autoOpenModal="contato" />} />
                    <Route path="*" element={<NotFoundTracker />} />
                  </Routes>
                </main>

                {/* Fullscreen Modals */}
                <FullscreenModal isOpen={activeModal === 'instituto'} onClose={closeModal} title="O Instituto" hashId="instituto">
                  <InstitutoContent />
                </FullscreenModal>
                <ProjetosHub
                  isOpen={activeModal === 'atuacao' || activeModal === 'projetos' || (activeModal && activeModal.startsWith('projeto-'))}
                  onClose={closeModal}
                  initialProject={activeModal?.startsWith('projeto-') ? activeModal.replace('projeto-', '') : null}
                />
                <FullscreenModal isOpen={activeModal === 'contato'} onClose={closeModal} title="Contato" hashId="contato">
                  <ContatoContent />
                </FullscreenModal>

                <FullscreenModal isOpen={activeModal === 'deia'} onClose={closeModal} title="DEI&A" hashId="deia"
                  breadcrumb={[{ label: 'DEI&A' }]}>
                  <DEIAContent />
                </FullscreenModal>

                <FullscreenModal isOpen={activeModal === 'emfoco' || activeModal?.startsWith('emfoco:')} onClose={closeModal} title="Em Foco" hashId="emfoco"
                  breadcrumb={emfocoBreadcrumb}
                  scrollContainerRef={emfocoScrollRef}>
                  <EmFocoContent
                    initialSlug={activeModal?.startsWith('emfoco:') ? activeModal.replace('emfoco:', '') : null}
                    onBreadcrumbChange={setEmfocoBreadcrumb}
                    scrollContainerRef={emfocoScrollRef}
                  />
                </FullscreenModal>

                <FullscreenModal isOpen={activeModal === 'artigos' || activeModal?.startsWith('artigos:')} onClose={closeModal} title="Artigos" hashId="artigos"
                  breadcrumb={artigosBreadcrumb}
                  scrollContainerRef={artigosScrollRef}>
                  <ArtigosContent
                    initialSlug={activeModal?.startsWith('artigos:') ? activeModal.replace('artigos:', '') : null}
                    onBreadcrumbChange={setArtigosBreadcrumb}
                    scrollContainerRef={artigosScrollRef}
                  />
                </FullscreenModal>

                <FullscreenModal isOpen={activeModal === 'solucoes'} onClose={closeModal} title="Soluções" hashId="solucoes"
                  breadcrumb={[{ label: 'Soluções' }]}>
                  <SolucoesContent />
                </FullscreenModal>

                <FullscreenModal isOpen={activeModal === 'acessibilidade'} onClose={closeModal} title="Acessibilidade" hashId="acessibilidade"
                  breadcrumb={[{ label: 'Acessibilidade' }]}>
                  <AcessibilidadeContent />
                </FullscreenModal>

                <Toaster position="top-center" />
              </div>
            } />
          </Routes>
          </Suspense>
          <CookieConsent />
        </BrowserRouter>
      </ModalContext.Provider>
      </I18nProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
